import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ComboboxItem, Group, Menu } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconEye,
  IconPencil,
  IconStack2,
  IconTrash,
} from "@tabler/icons-react";

import { openDeleteModal } from "@components/DeleteModal";
import { FilePreviewModal } from "@components/FilePreviewModal";
import { LastUpdatedTimestamp } from "@components/LastUpdated";

import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { useDeleteLab } from "@hooks/mutations/lab/useDeleteLab";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { useAuth } from "@contexts/auth";

import { Laboratory } from "@customTypes/laboratory";

import { EditModal } from "../EditModal";
import { LabGroupModal } from "../LabGroupModal";


interface TableControlsProps {
  lab: Partial<Laboratory>;
  labTypes: ComboboxItem[];
}

export const TableControls = ({ lab, labTypes }: TableControlsProps) => {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [
    filePreviewOpened,
    { open: filePreviewOpen, close: filePreviewClose },
  ] = useDisclosure(false);
  const [editModalOpened, { open: editModalOpen, close: editModalClose }] =
    useDisclosure(false);
  const [
    labGroupModalOpened,
    { open: labGroupModalOpen, close: labGroupModalClose },
  ] = useDisclosure(false);
  const [labState, setLabState] = useState<Partial<Laboratory> | null>(null);

  const { mutate: deleteLab, isLoading: isDeleteLabLoading } = useDeleteLab();
  const handleErrors = useQueryErrorHandling();

  useEffect(() => {
    if (lab) {
      // console.log(row.original);
      setLabState(lab);
    }
  }, [lab]);

  return (
    <>
      <FilePreviewModal
        opened={filePreviewOpened}
        close={filePreviewClose}
        docs={
          labState?.files
            ? [
                ...labState.files.map(item => ({
                  uri: "https://" + item + `?token=${token}`,
                  fileName: (item as string).split("/files/")[1],
                })),
              ]
            : []
        }
      />
      <EditModal
        opened={editModalOpened}
        close={editModalClose}
        lab={lab}
        labTypes={labTypes}
      />
      <LabGroupModal
        opened={labGroupModalOpened}
        close={labGroupModalClose}
        lab={lab}
      />

      <Group className="table-controls" justify="end">
        <Menu withArrow position="bottom-end" withinPortal>
          <Menu.Target>
            <Button
              variant="outline"
              size="compact-sm"
              loading={isDeleteLabLoading}>
              {t("action")}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              disabled={
                labState?.files && labState.files.length > 0 ? false : true
              }
              leftSection={<IconEye size={16} stroke={1.5} />}
              onClick={filePreviewOpen}>
              {t("view-files")}
            </Menu.Item>

            <PermissionGate permission="labs.edit"><Menu.Item
              leftSection={<IconPencil size={16} stroke={1.5} />}
              onClick={editModalOpen}>
              {t("edit-lab")}
            </Menu.Item></PermissionGate>
            <PermissionGate permission="labs.edit"><Menu.Item
              disabled={
                (labState as Partial<Laboratory>)?.labGroupId ? false : true
              }
              leftSection={<IconStack2 size={16} stroke={1.5} />}
              onClick={labGroupModalOpen}>
              {t("update-lab-group")}
            </Menu.Item></PermissionGate>
            <PermissionGate permission={["labs.delete", "labs.archive"]}><Menu.Item
              leftSection={<IconTrash size={16} stroke={1.5} />}
              color="red"
              onClick={() =>
                openDeleteModal({
                  onConfirm: () => {
                    const id = showLoadingNotification({});
                    deleteLab(
                      {
                        labId: labState?.id as string,
                        patientId: labState?.patientId as string,
                      },
                      {
                        onSuccess: () => {
                          showSuccessNotification({
                            id,
                            title: "success",
                            message: "delete-lab-success",
                          });
                        },
                        onError: (res: any) => {
                          handleErrors(res, "mutation", id);
                        },
                      }
                    );
                  },
                })
              }>
              {t("delete-lab")}
            </Menu.Item></PermissionGate>
          </Menu.Dropdown>
        </Menu>
      </Group>

      <LastUpdatedTimestamp
        updatedDate={lab?.updatedAt as Date}
        className="last-updated"
      />
    </>
  );
};
